import { Box, Typography, styled } from "@mui/material";
import { colors } from "../../../../theme/colors";
import { TABLE_GRID_COLUMNS } from "../TreatmentAppointment/TreatmentAppointment.styled";

export const EditAppointmentDescriptionField = styled(Box)({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  flex: 1,
});

export const EditAppointmentDescriptionInput = styled(Box)({
  flex: 1,
  minWidth: 0,
});

export const EditAppointmentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSaving",
})<{ isSaving?: boolean }>(({ isSaving }) => ({
  border: `1px dashed ${colors.primary.main}`,
  borderRadius: "10px",
  marginBottom: "10px",
  position: "relative",
  ...(isSaving && {
    pointerEvents: "none",
    opacity: 0.7,
  }),
}));

export const EditAppointmentHeader = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: TABLE_GRID_COLUMNS,
  gap: "16px",
  alignItems: "flex-start",
  padding: "10px 12px",
  marginBottom: "12px",
  borderRadius: "10px",
  backgroundColor: colors.background.lightBlue,
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: "8px",
  },
}));

export const EditAppointmentFee = styled(Typography)({
  fontWeight: 700,
  fontFamily: "Nunito",
  fontSize: "14px",
  color: colors.text.primary,
});

export const EditAppointmentHeaderActions = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  gap: "8px",
});

export const EditProcedureRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragging" && prop !== "isDragOver",
})<{ isDragging?: boolean; isDragOver?: boolean }>(({ theme, isDragging, isDragOver }) => ({
  display: "grid",
  gridTemplateColumns: TABLE_GRID_COLUMNS,
  gap: "16px",
  alignItems: "flex-start",
  padding: "9px 12px 12px 12px",
  borderBottom: `1px solid ${colors.background.default}`,
  borderRadius: isDragging || isDragOver ? "8px" : undefined,
  backgroundColor: isDragOver ? colors.background.lightBlue : "transparent",
  opacity: isDragging ? 0.5 : 1,
  boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.15)" : "none",
  transition:
    "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease",
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: "8px",
  },
}));

export const EditProcedureNameCell = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  minWidth: 0,
  gap: "6px",
});

export const EditProcedureDragHandle = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  cursor: "grab",
  marginTop: "10px",
  "&:active": {
    cursor: "grabbing",
  },
});

export const EditProcedureNameInput = styled(Box)({
  flex: 1,
  minWidth: 0,
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
});

export const EditAddStepRow = styled(Box)({
  padding: "8px 12px 12px 12px",
  "& span": {
    marginRight: "4px!important",
  },
});

export const EditAppointmentTimeframeRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: TABLE_GRID_COLUMNS,
  gap: "16px",
  padding: "8px 12px",
  borderTop: `1px solid ${colors.background.default}`,
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: "8px",
  },
}));

export const EditTimeframeBar = styled(Box)({
  gridColumn: "1",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px",
  backgroundColor: colors.primary.main,
  borderRadius: "8px",
  minWidth: 0,
  width: "100%",
});

export const EditTimeframeInput = styled("input")({
  flex: 1,
  minWidth: 0,
  width: "100%",
  height: "30px",
  padding: "0 12px",
  border: "none",
  borderRadius: "8px",
  backgroundColor: colors.background.secondary,
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  color: colors.text.primary,
  outline: "none",
  "&::placeholder": {
    color: colors.text.secondary,
    opacity: 1,
  },
});

export const EditTimeframeArrow = styled(Box)({
  display: "flex",
  width: "20px",
  flexShrink: 0,
  transform: "rotate(90deg)",
});

export const EditProcedureCdtCell = styled(Box)({
  minWidth: 0,
});

export const EditProcedureCdtValue = styled(Box)({
  fontFamily: "Nunito",
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  alignItems: "center",
  fontSize: "14px",
});

export const EditProcedureCdtCode = styled(Box)({
  fontWeight: 600,
  color: colors.text.action,
  fontFamily: "Nunito",
  fontSize: "14px",
  whiteSpace: "nowrap",
  padding: "0 4px 0 8px",
  height: "24px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  backgroundColor: colors.background.lightBlue,
  borderRadius: "16px",
  border: `1px solid ${colors.text.action}`,
});

export const EditProcedureCdtCodeRemove = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: colors.text.action,
  lineHeight: 0,
});

export const EditAddCodeButtonWrapper = styled(Box)({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
  width: "100%",
});

export const EditAddCodeButton = styled("button")({
  fontWeight: 600,
  color: colors.border.medium,
  fontFamily: "Nunito",
  fontSize: "14px",
  whiteSpace: "nowrap",
  padding: "0 8px",
  height: "24px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "transparent",
  borderRadius: "16px",
  border: `1px dashed ${colors.border.medium}`,
  cursor: "pointer",
});

export const EditAppointmentMetaFields = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexShrink: 0,
});

export const EditOptionContainer = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  backgroundColor: colors.background.darkBlue,
  borderRadius: "6px",
  overflow: "hidden",
  height: "30px",
});

export const EditOptionLabel = styled(Box)({
  color: colors.text.white,
  fontFamily: "Nunito",
  fontSize: "14px",
  fontWeight: 700,
  padding: "0 10px",
  whiteSpace: "nowrap",
  lineHeight: "30px",
});

export const EditOptionSelect = styled("select")({
  height: "30px",
  border: `1px solid ${colors.background.darkBlue}`,
  borderRadius: "6px",
  backgroundColor: colors.background.secondary,
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  color: colors.text.primary,
  padding: "0 24px 0 8px",
  outline: "none",
  cursor: "pointer",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%230E426D' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 8px center",
  minWidth: "50px",
});

export const EditDurationField = styled(Box)({
  display: "flex",
  alignItems: "center",
});

export const EditDurationInput = styled("input")({
  width: "52px",
  height: "30px",
  padding: "0 8px",
  border: `1px solid ${colors.border.medium}`,
  borderRadius: "8px",
  backgroundColor: colors.background.secondary,
  fontFamily: "Nunito, sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  color: colors.text.primary,
  outline: "none",
  textAlign: "center",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  MozAppearance: "textfield",
});

export const EditDurationSuffix = styled(Typography)({
  fontFamily: "Nunito",
  fontSize: "14px",
  fontWeight: 700,
  color: colors.background.darkBlue,
  marginLeft: "6px",
  whiteSpace: "nowrap",
});
